import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarketStore, ListingSide } from '../../stores/marketStore';
import { useAppStore } from '../../App';
import { FamilyAvatar } from '../../components/FamilyAvatar';
import { formatNumber, formatWon, priceTooltipText } from '../../utils/number';
import BarterRegisterModal from './BarterRegisterModal';
import ListingCreateModal from './ListingCreateModal';

const TABS: { side: ListingSide; label: string; description: string }[] = [
 {
  side: 'seeking_advertiser',
  label: '스크린이 광고주를 찾습니다',
  description: '광고 또는 무료 콘텐츠를 찾는 스크린 운영사들입니다.',
 },
 {
  side: 'seeking_media',
  label: '광고주가 스크린을 찾습니다',
  description: '스크린을 찾는 광고주 혹은 콘텐츠 제작자들입니다.',
 },
 {
  side: 'seeking_member',
  label: '모패밀리가 구성원을 찾습니다',
  description: '함께할 모패밀리를 모집하는 곳입니다. 다른 모패밀리와 합쳐 더 큰 패밀리로 운영을 확장할 수 있습니다.',
 },
 {
  side: 'barter',
  label: '슬롯 교환을 찾습니다',
  description: '서로의 모패밀리에 슬롯을 한 개씩 내어주고 상대 영상을 자유롭게 송출할 수 있게 하는 1:1 슬롯 교환(바터) 매물을 모은 곳입니다.',
 },
];

const MarketLanding: React.FC = () => {
 const navigate = useNavigate();
 const user = useAppStore(s => s.user);
 const {
  filter, page, limit, rows, total, isLoading, error,
  industries, regions, setFilter, setPage, fetchListings, fetchMeta,
 } = useMarketStore();
 const [showBarterModal, setShowBarterModal] = useState(false);
 // 교환 외 매물 등록(광고 받기·미디어 찾기·구성원 모집) — 모패밀리 선택 → 등록 폼.
 const [showCreateModal, setShowCreateModal] = useState(false);
 const createSide = (filter.side === 'seeking_advertiser' || filter.side === 'seeking_media' || filter.side === 'seeking_member')
  ? filter.side : undefined;

 useEffect(() => { fetchMeta(); }, [fetchMeta]);
 useEffect(() => { fetchListings(); }, [filter, page, fetchListings]);

 const totalPages = Math.max(1, Math.ceil(total / limit));

 const sidos = useMemo(() => [...new Set(regions.map(r => r.sido))], [regions]);
 const guguns = useMemo(
  () => filter.sido ? regions.filter(r => r.sido === filter.sido).map(r => r.gugun) : [],
  [regions, filter.sido]
 );

 const activeTab = TABS.find(t => t.side === filter.side) ?? TABS[0];

 return (
  <div>
   <div className="flex items-center justify-between gap-2 mb-2">
    <h1 className="text-2xl md:text-3xl font-bold text-gray-800">마켓</h1>
    {filter.side === 'barter' && user && (
     <button
      onClick={() => setShowBarterModal(true)}
      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm whitespace-nowrap"
     >
      슬롯 교환 매물 등록
     </button>
    )}
    {filter.side !== 'barter' && user && (
     <button
      onClick={() => setShowCreateModal(true)}
      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm whitespace-nowrap"
     >
      매물 등록
     </button>
    )}
   </div>
   <p className="text-sm text-gray-500 leading-relaxed mb-4">{activeTab.description}</p>

   {/* 탭 */}
   <div className="border-b border-gray-200 mb-4">
    <nav className="flex gap-4 -mb-px overflow-x-auto">
     {TABS.map(t => (
      <button
       key={t.side}
       onClick={() => setFilter({ side: t.side })}
       className={`py-2 px-1 border-b-2 text-sm whitespace-nowrap ${
        filter.side === t.side
         ? 'border-blue-500 text-blue-600 font-medium'
         : 'border-transparent text-gray-500 hover:text-gray-700'
       }`}
      >
       {t.label}
      </button>
     ))}
    </nav>
   </div>

   {/* 필터 */}
   <div className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-3 mb-4">
    <input
     type="text"
     placeholder="검색 (제목/설명)"
     value={filter.q ?? ''}
     onChange={e => setFilter({ q: e.target.value })}
     className="px-2 py-1 border border-gray-300 text-sm"
    />
    <select
     value={filter.industry ?? ''}
     onChange={e => setFilter({ industry: e.target.value || undefined })}
     className="px-2 py-1 border border-gray-300 text-sm"
    >
     <option value="">업종 전체</option>
     {industries.map(i => (
      <option key={i.code} value={i.code}>{i.ko_name}</option>
     ))}
    </select>
    <select
     value={filter.sido ?? ''}
     onChange={e => setFilter({ sido: e.target.value || undefined, gugun: undefined })}
     className="px-2 py-1 border border-gray-300 text-sm"
    >
     <option value="">시·도 전체</option>
     {sidos.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
    <select
     value={filter.gugun ?? ''}
     onChange={e => setFilter({ gugun: e.target.value || undefined })}
     disabled={!filter.sido}
     className="px-2 py-1 border border-gray-300 text-sm disabled:opacity-50"
    >
     <option value="">구·군 전체</option>
     {guguns.map(g => <option key={g} value={g}>{g}</option>)}
    </select>
   </div>

   {/* 가격대 슬라이더 — 0.1원 ~ 10원/회. 구성원/바터 탭은 가격 무관이라 숨김. */}
   <div className={`mb-4 text-sm text-gray-600 ${(filter.side === 'seeking_member' || filter.side === 'barter') ? 'hidden' : ''}`}>
    <label className="mr-2">가격대 (원/회):</label>
    <input
     type="number"
     step="0.1"
     min="0"
     placeholder="최소"
     value={filter.priceMin ?? ''}
     onChange={e => setFilter({ priceMin: e.target.value === '' ? undefined : Number(e.target.value) })}
     className="w-20 px-2 py-1 border border-gray-300 text-sm mr-1"
    />
    <span className="mr-1">~</span>
    <input
     type="number"
     step="0.1"
     min="0"
     placeholder="최대"
     value={filter.priceMax ?? ''}
     onChange={e => setFilter({ priceMax: e.target.value === '' ? undefined : Number(e.target.value) })}
     className="w-20 px-2 py-1 border border-gray-300 text-sm"
    />
   </div>

   {error && <div className="bg-red-50 text-red-700 p-2 text-sm mb-4">{error}</div>}
   {isLoading && <div className="text-gray-500 text-sm py-8 text-center">로딩 중...</div>}
   {!isLoading && rows.length === 0 && (
    <div className="text-gray-400 text-sm py-12 text-center">조건에 맞는 리스팅이 없습니다.</div>
   )}

   {/* 결과 그리드 */}
   <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-3">
    {rows.map(r => (
     <button
      key={r.listing_hash}
      onClick={() => navigate(`/market/${r.listing_hash}`)}
      className="text-left bg-white border border-gray-200 p-3 hover:border-blue-400 transition-colors"
     >
      <div className="flex items-start justify-between gap-2 mb-1">
       <h3 className="text-sm md:text-base font-medium truncate flex-1">{r.title}</h3>
       {r.is_expired && (
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 flex-shrink-0">만료</span>
       )}
       {!r.is_active && !r.is_expired && (
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 flex-shrink-0">비활성</span>
       )}
      </div>
      {r.family_name && (
       <div className="flex items-center gap-2 mb-1">
        <FamilyAvatar
         name={r.family_name}
         familyHash={r.mo_family_hash}
         imageUrl={r.family_image_url}
         size={48}
        />
        <span className="text-sm text-gray-700 truncate">{r.family_name}</span>
       </div>
      )}
      {r.owner_name && (
       <div className="flex items-center gap-1.5 mb-1">
        <FamilyAvatar
         name={r.owner_name}
         familyHash={r.owner_mo_peer_hash || r.owner_name}
         imageUrl={r.owner_profile_image}
         size={20}
        />
        <span className="text-xs text-gray-400 truncate">{r.owner_name}</span>
       </div>
      )}
      <div className="text-xs text-gray-600 space-y-0.5">
       <div>{String(r.period_start).slice(0, 10)} ~ {String(r.period_end).slice(0, 10)}</div>
       {r.side !== 'seeking_member' && r.side !== 'barter' && (
        <div
         className="cursor-help underline decoration-dotted decoration-gray-400 inline-block"
         title={priceTooltipText(r.price_per_impression)}
        >
         {formatWon(r.price_per_impression)}/회
        </div>
       )}
       {r.side === 'barter' && (
        <div className="text-purple-600 font-medium">슬롯 교환 (1:1 바터)</div>
       )}
       {(r.region_sido || r.region_gugun) && <div>{r.region_sido} {r.region_gugun}</div>}
       {r.side === 'seeking_advertiser' && typeof r.screen_count === 'number' && (
        <div>총 스크린 {formatNumber(r.screen_count)}대</div>
       )}
      </div>
      {/* 스크린 보유 매물 미리보기 — 왼쪽: 첫 스크린의 설치 사진, 오른쪽: 지금 외부로 내보내는 첫 영상 */}
      {(r.screen_preview || r.outbound_preview) && (
       <div className="flex gap-2 mt-2">
        {r.screen_preview && (
         <div className="flex-1 min-w-0">
          <img src={r.screen_preview.photo_url} alt="" className="w-full aspect-video object-cover border border-gray-200" />
          <div className="text-[10px] text-gray-500 truncate mt-0.5">스크린 · {r.screen_preview.name}</div>
         </div>
        )}
        {r.outbound_preview && (
         <div className="flex-1 min-w-0">
          <img src={`/mofile/thumbnail/${r.outbound_preview.thumbnail_path}`} alt="" className="w-full aspect-video object-cover border border-gray-200" />
          <div className="text-[10px] text-gray-500 truncate mt-0.5">내보내는 중 · {r.outbound_preview.filename}</div>
         </div>
        )}
       </div>
      )}
      {/* 광고주 listing 의 광고 카탈로그 미리보기 — 슬롯 점유 영상 썸네일 (최대 4장) */}
      {r.thumbnails && r.thumbnails.length > 0 && (
       <div className="flex gap-1 mt-2">
        {r.thumbnails.map(t => (
         <img
          key={t}
          src={`/mofile/thumbnail/${t}`}
          alt=""
          className="w-12 h-7 object-cover border border-gray-200"
         />
        ))}
       </div>
      )}
      {r.description && (
       <p className="text-xs text-gray-500 mt-2 line-clamp-2">{r.description}</p>
      )}
     </button>
    ))}
   </div>

   {/* 페이지네이션 */}
   {totalPages > 1 && (
    <div className="flex items-center justify-center gap-2 mt-6">
     <button
      onClick={() => setPage(Math.max(0, page - 1))}
      disabled={page === 0}
      className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
     >
      이전
     </button>
     <span className="text-sm text-gray-600">{page + 1} / {totalPages}</span>
     <button
      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
      disabled={page >= totalPages - 1}
      className="px-3 py-1 border border-gray-300 text-sm disabled:opacity-50"
     >
      다음
     </button>
    </div>
   )}

   {showBarterModal && (
    <BarterRegisterModal
     onClose={() => setShowBarterModal(false)}
     onCreated={() => fetchListings()}
    />
   )}
   {showCreateModal && (
    <ListingCreateModal
     initialSide={createSide}
     onClose={() => setShowCreateModal(false)}
     onCreated={() => { setShowCreateModal(false); fetchListings(); }}
    />
   )}
  </div>
 );
};

export default MarketLanding;
